import { Button, Input, Typography } from '@breetometer/components';
import { BvnValidationIcon } from 'BreetAssets';
import { DatePicker } from 'BreetComponents';
import { handleApiError, preventFormDefault, showToast } from 'BreetHelpers';
import { useFormData, useModalApi } from 'BreetHooks';
import { useGenerateVirtualAccountMutation, useUpdateKybMutation } from 'BreetRedux';
import { format, parseISO, subYears } from 'date-fns';
import { useCallback, useMemo } from 'react';

import { BvnValidationSchema } from './DialogBvnValidation.helpers';

const DialogBvnValidation = () => {
	const [updateKyb, { isLoading: isUpdatingKyb }] = useUpdateKybMutation();

	const [generateVbafn, { isLoading: isGenerateVbaLoading }] = useGenerateVirtualAccountMutation();

	const { closeModalDirect } = useModalApi();

	const bvnValidationFormData = useFormData(BvnValidationSchema, { directorBvn: '', directorDob: '' }, { mode: 'onChange' });

	const { hookFormProps } = bvnValidationFormData;

	// A director's date of birth: never in the future, and within a sane 120-year window.
	const today = useMemo(() => new Date(), []);
	const earliestDob = useMemo(() => subYears(today, 120), [today]);

	const directorDob = hookFormProps.watch('directorDob');
	const dobValue = directorDob ? parseISO(directorDob) : null;

	const handleDobChange = useCallback(
		(date: Date | null) => {
			hookFormProps.setValue('directorDob', date ? format(date, 'yyyy-MM-dd') : '', {
				shouldValidate: true,
				shouldDirty: true,
			});
		},
		[hookFormProps]
	);

	const validateBvn = useCallback(() => {
		updateKyb({
			directorBvn: bvnValidationFormData.hookFormProps.watch('directorBvn'),
			directorDob: bvnValidationFormData.hookFormProps.watch('directorDob'),
		})
			.unwrap()
			.then(() => {
				showToast('success', 'BVN validated');

				generateVbafn({
					currency: 'ngn',
				})
					.unwrap()
					.then(() => {
						showToast('success', 'Virtual account generated successfully');
					})
					.catch(handleApiError)
					.finally(() => {
						bvnValidationFormData.hookFormProps.reset({
							directorBvn: '',
							directorDob: '',
						});
						closeModalDirect('bvnValidation');
					});
			})
			.catch(handleApiError);
	}, [bvnValidationFormData.hookFormProps, closeModalDirect, generateVbafn, updateKyb]);

	return (
		<div className='bvn-validation bg-primary-500'>
			<div className='bvn-validation-header'>
				<BvnValidationIcon className='bvn-header-icon' />
				<Typography
					as='h4'
					className='color-neutral-50'
				>
					BVN Validation Required
				</Typography>
				<Typography className='color-primary-100'>
					A director&apos;s BVN is required to generate a virtual bank account for your business.
				</Typography>
			</div>

			<form
				className='new-drop-down-input'
				onSubmit={preventFormDefault}
			>
				<Input
					label="Director's BVN"
					id='director-bvn'
					widthType='full'
					placeholder=' '
					maxLength={11}
					{...bvnValidationFormData.hookFormProps.register('directorBvn')}
					errorMessage={bvnValidationFormData.hookFormProps.formState.errors.directorBvn?.message}
					required
				/>

				<DatePicker
					label='Date of Birth'
					id='date-of-birth'
					value={dobValue}
					onChange={handleDobChange}
					minDate={earliestDob}
					maxDate={today}
					errorMessage={hookFormProps.formState.errors.directorDob?.message}
					calendarIconVariant='bvn'
					required
				/>

				<div className='bt_button-container'>
					<Button
						onClick={validateBvn}
						isLoading={isUpdatingKyb || isGenerateVbaLoading}
						className='primary-hover'
						disabled={!bvnValidationFormData.isFormCompleted || !bvnValidationFormData.hookFormProps.formState.isDirty}
						type='button'
					>
						Validate
					</Button>
				</div>
			</form>
		</div>
	);
};

export default DialogBvnValidation;
